import { useEffect, useState } from "react";

import { toast } from "react-toastify";

import { TAvailability, TGoogleStatus, TPolicy } from "../@types";
import { apis, callApi } from "../api";
import { eventNames, logEvent } from "../utils/logEvent";

type TTeachingPolicyService = {
	listeners: (() => void)[];
	loading: boolean;
	policy: TPolicy | null;
	availableHours: TAvailability[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	setLoading: (loading: boolean) => void;
	savePolicy: (policy: TPolicy) => void;
	saveAvailableHours: (availableHours: TAvailability[]) => void;
	saveGoogleStatus: (status: TGoogleStatus) => void;
	clearData: () => void;
	emitChanges: () => void;
};

export const TeachingPolicyService: TTeachingPolicyService = {
	listeners: [],
	loading: false,
	policy: null,
	availableHours: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	savePolicy(policy) {
		this.policy = policy;
	},

	saveAvailableHours(availableHours) {
		this.availableHours = availableHours;
	},

	saveGoogleStatus(status) {
		if (this.policy) {
			this.policy = { ...this.policy, calendarToken: status };
		}
	},

	clearData() {
		this.policy = null;
		this.availableHours = [];
		this.emitChanges();
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

export function useTeachingPolicyInit() {
	useEffect(() => {
		const listener = () => {
			const status = localStorage.getItem("google_status");
			if (status) {
				TeachingPolicyService.saveGoogleStatus(status as TGoogleStatus);
				TeachingPolicyService.emitChanges();
				localStorage.removeItem("google_status");
			}
		};
		window.addEventListener("storage", listener);

		return () => {
			window.removeEventListener("storage", listener);
		};
	}, []);

	useEffect(() => {
		const getData = async () => {
			try {
				TeachingPolicyService.setLoading(true);
				TeachingPolicyService.emitChanges();
				const promiseGetPolicy = callApi({
					method: "GET",
					url: apis.POLICY,
				});

				const promiseGetAvailableHours = callApi({
					method: "GET",
					url: apis.POLICY_OPERATION_HOURS,
				});

				const responseGetPolicy = await promiseGetPolicy;
				const responseGetAvailableHours = await promiseGetAvailableHours;

				if (!responseGetPolicy || !responseGetAvailableHours) {
					return;
				}

				TeachingPolicyService.saveAvailableHours(responseGetAvailableHours);
				TeachingPolicyService.savePolicy(responseGetPolicy);
			} catch (error) {
			} finally {
				TeachingPolicyService.setLoading(false);
				TeachingPolicyService.emitChanges();
			}
		};

		getData();
	}, []);
}

export type TAvailableHour = {
	dayOfWeek: number;
	startHour: number;
	startMinute: number;
	endHour: number;
	endMinute: number;
};

export function useTeachingPolicy() {
	const [loading, setLoading] = useState(TeachingPolicyService.loading);
	const [policy, setPolicy] = useState(TeachingPolicyService.policy);
	const [availableHours, setAvailableHours] = useState(
		TeachingPolicyService.availableHours,
	);

	useEffect(() => {
		const updateData = () => {
			setLoading(TeachingPolicyService.loading);
			setPolicy(TeachingPolicyService.policy);
			setAvailableHours(TeachingPolicyService.availableHours);
		};

		TeachingPolicyService.addEventListener(updateData);

		return () => {
			TeachingPolicyService.removeEventListener(updateData);
		};
	}, []);

	const saveGeneralAvailability = async (
		newAvailableHours: TAvailableHour[],
	) => {
		try {
			const response = await callApi({
				method: "POST",
				url: apis.POLICY_OPERATION_HOURS,
				body: {
					schedules: newAvailableHours.map((availableHour) => {
						if (availableHour.endHour === 24 && availableHour.endMinute === 0) {
							return {
								...availableHour,
								endHour: 0,
								endMinute: 0,
							};
						}

						return availableHour;
					}),
				},
			});

			if (!response) {
				return;
			}

			logEvent(eventNames.setGeneralAvailability);

			toast.success("Saved changes!");
			TeachingPolicyService.saveAvailableHours(response);
			TeachingPolicyService.emitChanges();
		} catch (error) {}
	};

	const savePolicy = async (body: Partial<TPolicy>) => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: apis.POLICY,
				body,
			});

			if (!response) {
				return;
			}

			if (Object.prototype.hasOwnProperty.call(body, "timeBreak")) {
				logEvent(eventNames.setTimeBreak);
			}
			if (Object.prototype.hasOwnProperty.call(body, "sameDayNoticePeriod")) {
				logEvent(eventNames.setSameDayNotice);
			}
			if (Object.prototype.hasOwnProperty.call(body, "rescheduleFee")) {
				logEvent(eventNames.setSchedulingFee);
			}

			toast.success("Saved changes!");
			TeachingPolicyService.savePolicy(response.coursePolicy);
			TeachingPolicyService.emitChanges();
		} catch (error) {}
	};

	const linkWithGoogleCalendar = async () => {
		try {
			const response = await callApi({
				method: "GET",
				url: apis.GOOGLE_AUTH,
			});

			if (!response?.authURL) {
				return;
			}

			return response.authURL;
		} catch (error) {}
	};

	const unlinkWithGoogleCalendar = async () => {
		try {
			const response = await callApi({
				method: "DELETE",
				url: apis.GOOGLE_UNLINK,
			});

			if (!response) {
				return;
			}

			TeachingPolicyService.saveGoogleStatus(undefined);
			TeachingPolicyService.emitChanges();
		} catch (error) {}
	};

	return {
		loading,
		policy,
		availableHours,
		saveGeneralAvailability,
		savePolicy,
		linkWithGoogleCalendar,
		unlinkWithGoogleCalendar,
	};
}
